import { responsivePositionTranslator } from "../../services/responsive/ResponsivePositionTranslator";
import type { Component } from "../../types";
import { migrateLegacyTemplateComponents } from "../../utils/componentTreeMigrations";
import { migrateProjectThemeColors } from "../../utils/project-theme-color-migration";
import { rebuildParentIds, validateHierarchy } from "../../utils/rebuildParentIds";
import { enhanceComponentsWithChildren } from "./editor-utils";
import {
  buildComponent,
  mergeIdMap,
  toIdMap,
} from "./editor-store-helpers";
import {
  notifyResponsiveComponentAdded,
  pushComponentSnapshot,
} from "./component-slice-helpers";
import type {
  ComponentSlice,
  ComponentSliceState,
} from "./component-slice-types";
import type {
  EditorImmerGetState,
  EditorImmerSetState,
} from "./editor-store-types";
import type { CollaborationBridgeStore } from "./collaboration-bridge-types";
import { VersionIntegrationContext } from "./versioning-integration";

function syncChildrenFromParentIds(components: Component[]): Component[] {
  const componentIds = new Set(components.map((component) => component.id));
  const childIdsByParentId = new Map<string, string[]>();

  components.forEach((component) => {
    if (!component.parentId || !componentIds.has(component.parentId)) {
      return;
    }

    const childIds = childIdsByParentId.get(component.parentId) ?? [];
    childIds.push(component.id);
    childIdsByParentId.set(component.parentId, childIds);
  });

  return components.map((component) => {
    const parentChildIds = childIdsByParentId.get(component.id);
    if (!parentChildIds) {
      return component;
    }

    const existingChildren = component.children.filter((childId) =>
      componentIds.has(childId),
    );
    const mergedChildren = [
      ...existingChildren,
      ...parentChildIds.filter((childId) => !existingChildren.includes(childId)),
    ];

    return {
      ...component,
      children: mergedChildren,
    };
  });
}

export function createComponentCreateActions<
  TState extends ComponentSliceState,
>(
  set: EditorImmerSetState<TState>,
  get: EditorImmerGetState<TState>,
  getCollaborationBridge: () => CollaborationBridgeStore | null,
): Pick<ComponentSlice, "setComponents" | "addComponent" | "addMultipleComponents"> {
  return {
    setComponents: (components) => {
      const rebuiltComponents = rebuildParentIds(components);
      validateHierarchy(rebuiltComponents);

      const normalizedComponents = migrateProjectThemeColors(migrateLegacyTemplateComponents(
        rebuiltComponents.map((component) => ({
          ...component,
          responsivePosition:
            component.responsivePosition ??
            responsivePositionTranslator.generateDefaultResponsivePosition(
              component.type,
            ),
        })),
      ));
      const enhancedComponents =
        enhanceComponentsWithChildren(normalizedComponents);
      const componentMap = toIdMap(enhancedComponents);

      set((state) => {
        state.components = enhancedComponents;
        state.componentMap = componentMap;
        state.history.present = [...enhancedComponents];

        if (state.currentPageId) {
          state.pageComponents.set(state.currentPageId, [...enhancedComponents]);
          state.pageHistoryMap.set(state.currentPageId, state.history);
        }
      });
    },

    addComponent: (componentData) => {
      const currentPageId = get().currentPageId;
      const component = buildComponent(
        componentData,
        currentPageId ?? "",
        get().project?.id,
      );
      const currentState = get();

      if (!currentState.project?.id) {
        console.warn("Adding component before project is fully loaded");
      }

      if (currentState.versioningEnabled) {
        try {
          VersionIntegrationContext.getInstance().trackComponentChange(
            "create",
            component.id,
            component.pageId,
            undefined,
            component,
            {
              description: `Created ${component.type} component: ${component.name}`,
            },
          );
        } catch (error) {
          console.warn("Version tracking failed for component creation:", error);
        }
      }

      try {
        const bridgeStore = getCollaborationBridge();
        if (bridgeStore) {
          const bridge = bridgeStore.getState();
          if (
            bridge.isCollaborationActive &&
            typeof bridge.interceptComponentAdd === "function"
          ) {
            bridge.interceptComponentAdd(componentData);
            return;
          }
        }
      } catch (error) {
        console.error(
          "🔄 COLLABORATION CHECK: Error accessing bridge, proceeding with local component add",
          {
            error: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
            componentId: component.id,
            componentType: component.type,
          },
        );

        if (typeof window !== "undefined") {
          window.Sentry?.captureException(error, {
            tags: {
              source: "collaboration-bridge",
              operation: "addComponent",
            },
            extra: {
              componentId: component.id,
              componentType: component.type,
            },
          });
        }
      }

      set((state) => {
        let nextComponents = [...state.components, component];

        if (component.parentId) {
          nextComponents = nextComponents.map((candidate) =>
            candidate.id !== component.parentId
              ? candidate
              : {
                  ...candidate,
                  children: candidate.children.includes(component.id)
                    ? candidate.children
                    : [...candidate.children, component.id],
                },
          );
        }

        const enhancedComponents = enhanceComponentsWithChildren(nextComponents);

        state.components = enhancedComponents;
        state.componentMap = toIdMap(enhancedComponents);

        if (!component.isSubComponent) {
          state.selectedComponents = [component.id];
        }

        state.isModified = true;
        state.lastUpdateTimestamp = Date.now();

        const targetPageId = component.pageId || state.currentPageId;
        if (targetPageId) {
          state.pageComponents.set(targetPageId, [...enhancedComponents]);
        } else {
          console.warn(
            "⚠️ [EDITOR] No valid page ID for component, adding to default page",
          );
          const defaultPage = state.pages.find((page) => page.isDefault);
          if (defaultPage) {
            component.pageId = defaultPage.id;
            state.pageComponents.set(defaultPage.id, [...enhancedComponents]);
          }
        }

        pushComponentSnapshot(state);
      });

      notifyResponsiveComponentAdded(component);

      const finalState = get();
      if (!finalState.project?.id) {
        console.warn("No project ID found - component may not persist");

        if (!finalState.project) {
          console.warn(
            "CRITICAL: No project loaded! Component added to empty project state",
          );
        }
      }
    },

    addMultipleComponents: (componentsData) => {
      const currentPageId = get().currentPageId;
      const components = migrateProjectThemeColors(migrateLegacyTemplateComponents(
        componentsData.map((componentData) =>
          buildComponent(componentData, currentPageId ?? "", get().project?.id),
        ),
      ));
      const targetPageId =
        currentPageId || components.find((component) => component.pageId)?.pageId;

      set((state) => {
        let enhancedPageComponents: Component[] = [];
        let enhancedNewComponents: Component[] = components;

        if (targetPageId) {
          const existingPageComponents =
            state.pageComponents.get(targetPageId) ||
            state.components.filter((component) => component.pageId === targetPageId);
          const mergedPageComponentsById = new Map(
            existingPageComponents.map((component) => [component.id, component]),
          );
          components.forEach((component) => {
            mergedPageComponentsById.set(component.id, component);
          });
          enhancedPageComponents = enhanceComponentsWithChildren(
            syncChildrenFromParentIds(Array.from(mergedPageComponentsById.values())),
          );

          state.pageComponents.set(targetPageId, enhancedPageComponents);
          enhancedNewComponents = enhancedPageComponents.filter((component) =>
            components.some((newComponent) => newComponent.id === component.id),
          );
        }

        const nextComponents = targetPageId
          ? enhancedPageComponents
          : enhanceComponentsWithChildren(
              syncChildrenFromParentIds([...state.components, ...components]),
            );

        state.components = nextComponents;
        state.componentMap = targetPageId
          ? toIdMap(nextComponents)
          : mergeIdMap(state.componentMap, enhancedNewComponents);

        if (enhancedNewComponents.length > 0) {
          state.selectedComponents = [enhancedNewComponents[0].id];
        }

        state.isModified = true;
        state.lastUpdateTimestamp = Date.now();

        pushComponentSnapshot(state);
      });
    },
  };
}
